const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export const API_ENDPOINTS = {
  // Auth
  login: `${API_BASE_URL}/api/v1/portal/auth/login`,
  refresh: `${API_BASE_URL}/api/v1/portal/auth/refresh`,
  logout: `${API_BASE_URL}/api/v1/portal/auth/logout`,
  setPassword: `${API_BASE_URL}/api/v1/portal/auth/set-password`,
  resetPassword: `${API_BASE_URL}/api/v1/portal/auth/reset-password`,
  me: `${API_BASE_URL}/api/v1/portal/auth/me`,

  // Licenses
  licenses: `${API_BASE_URL}/api/v1/portal/licenses`,
  license: (id: string) => `${API_BASE_URL}/api/v1/portal/licenses/${id}`,

  // Activations
  activations: `${API_BASE_URL}/api/v1/portal/activations`,
  activation: (id: string) => `${API_BASE_URL}/api/v1/portal/activations/${id}`,

  // Releases
  releases: `${API_BASE_URL}/api/v1/portal/releases`,
  releaseDownload: (id: string) => `${API_BASE_URL}/api/v1/portal/releases/${id}/download`,
};

class ApiClient {
  private getAccessToken(): string | null {
    return localStorage.getItem('portal_access_token');
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('portal_refresh_token');
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('portal_access_token', accessToken);
    localStorage.setItem('portal_refresh_token', refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem('portal_access_token');
    localStorage.removeItem('portal_refresh_token');
  }

  private async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(API_ENDPOINTS.refresh, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        this.clearTokens();
        return false;
      }

      const json = await response.json();
      // Backend wraps successful responses in { success: true, data: {...} }
      const data = json.data || json;
      this.setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      this.clearTokens();
      return false;
    }
  }

  async fetch<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    const accessToken = this.getAccessToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    let response = await fetch(url, { ...options, headers });

    // If unauthorized, try to refresh token
    if (response.status === 401 && accessToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.getAccessToken()}`;
        response = await fetch(url, { ...options, headers });
      } else {
        // Redirect to login
        window.location.href = '/login';
        throw new Error('Session expired');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    const json = await response.json();
    // Backend wraps successful responses in { success: true, data: {...} }
    if (json && json.success && json.data !== undefined) {
      return json.data;
    }
    return json;
  }

  async get<T>(url: string): Promise<T> {
    return this.fetch<T>(url, { method: 'GET' });
  }

  async post<T>(url: string, body?: unknown): Promise<T> {
    return this.fetch<T>(url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(url: string, body?: unknown): Promise<T> {
    return this.fetch<T>(url, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(url: string): Promise<T> {
    return this.fetch<T>(url, { method: 'DELETE' });
  }

  // Special method for downloads
  async download(url: string): Promise<{ url: string; fileName: string }> {
    const accessToken = this.getAccessToken();

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || 'Download failed');
    }

    const json = await response.json();
    // Backend wraps successful responses in { success: true, data: {...} }
    const data = json.data || json;
    return data;
  }
}

export const apiClient = new ApiClient();
